import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../App';

const COMMANDS = [
  'help', 'next', 'back', 'refresh', 'home', 'popular', 'news', 
  'clear', 'clear-history', 'ls', 'dir', 'cd', 'Get-Post'
];

function Footer({ onCommand, commandHistory, setCommandHistory }) {
  const { theme } = useTheme();
  const [inputValue, setInputValue] = useState('');
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [tempCommand, setTempCommand] = useState('');
  const [completionIndex, setCompletionIndex] = useState(-1);
  const [possibleCompletions, setPossibleCompletions] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setHistoryIndex(-1);
    setCompletionIndex(-1);
    setPossibleCompletions([]);
  };

  const findCompletions = (partial) => {
    const lower = partial.toLowerCase();
    return COMMANDS.filter(cmd => cmd.toLowerCase().startsWith(lower));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const partial = inputValue.trim();
      
      if (partial) {
        if (completionIndex === -1) {
          const completions = findCompletions(partial);
          if (completions.length > 0) {
            setPossibleCompletions(completions);
            setCompletionIndex(0);
            setInputValue(completions[0]);
          }
        } else {
          const nextIndex = (completionIndex + 1) % possibleCompletions.length;
          setCompletionIndex(nextIndex);
          setInputValue(possibleCompletions[nextIndex]);
        }
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex === -1 
          ? commandHistory.length - 1 
          : Math.max(0, historyIndex - 1);
        
        if (historyIndex === -1) {
          setTempCommand(inputValue);
        }
        
        setHistoryIndex(newIndex);
        setInputValue(commandHistory[newIndex]);
        setCompletionIndex(-1);
        setPossibleCompletions([]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex !== -1) {
        const newIndex = historyIndex + 1;
        
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1);
          setInputValue(tempCommand);
        } else {
          setHistoryIndex(newIndex);
          setInputValue(commandHistory[newIndex]);
        }
        setCompletionIndex(-1);
        setPossibleCompletions([]);
      }
    } else if (e.key === 'Escape') {
      setCompletionIndex(-1);
      setPossibleCompletions([]);
      setHistoryIndex(-1);
      setInputValue('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedCommand = inputValue.trim();
    
    if (trimmedCommand) {
      if (commandHistory[commandHistory.length - 1] !== trimmedCommand) {
        setCommandHistory(prev => [...prev, trimmedCommand]);
      }
      
      onCommand(trimmedCommand);
      setInputValue('');
      setHistoryIndex(-1);
      setTempCommand('');
      setCompletionIndex(-1);
      setPossibleCompletions([]);
    }
  };

  const cursorStyle = {
    display: 'inline-block',
    width: '8px',
    height: '1.2em',
    backgroundColor: theme.colors.foreground,
    animation: 'blink 1s step-end infinite',
    marginLeft: '5px',
  };

  return (
    <>
      {possibleCompletions.length > 1 && completionIndex >= 0 && (
        <div style={{ 
          marginTop: '10px', 
          marginBottom: '5px',
          color: theme.colors.accentCyan,
          fontSize: '12px'
        }}>
          Tab completions: {possibleCompletions.map((cmd, i) => (
            <span key={cmd} style={{ 
              marginLeft: '10px',
              color: i === completionIndex ? theme.colors.accentGreen : theme.colors.foreground 
            }}>
              {cmd}
            </span>
          ))}
        </div>
      )}
      <form 
        onSubmit={handleSubmit} 
        onClick={() => inputRef.current?.focus()}
        style={{ display: 'flex', alignItems: 'center', marginTop: '10px', cursor: 'text' }}
      >
        <span style={{ color: theme.colors.accentGreen }}>&#xe0b0;</span>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            style={{
              background: 'transparent',
              border: 'none',
              color: theme.colors.foreground,
              fontFamily: 'inherit',
              fontSize: 'inherit',
              width: '100%',
              outline: 'none',
              paddingLeft: '10px',
            }}
          />
          <div style={cursorStyle}></div>
        </div>
      </form>
    </>
  );
}

export default Footer;